import { useCallback, useEffect, useRef } from 'react';

import { ApiError } from '@/modules/api/api-client';
import { verifySession } from '@/modules/auth';
import { useAuthStore } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';

/**
 * Hook that verifies the user's session on initial app load and
 * whenever the browser tab regains focus (visibilitychange event).
 *
 * Behavior:
 * - On mount: calls GET /auth/me (via verifySession) with 5s timeout
 * - On tab visibility change (hidden → visible): re-verifies session
 * - On 401 response: calls setUnauthenticated (logout without redirect here)
 * - On 5xx or timeout: shows error toast notification without redirecting
 */
export function useSessionVerification(): void {
  const setUnauthenticated = useAuthStore((state) => state.setUnauthenticated);
  const addToast = useToastStore((state) => state.addToast);
  const verificationInProgress = useRef(false);
  const initialVerificationDone = useRef(false);

  const handleVerificationError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          setUnauthenticated();
        } else if (error.status >= 500 || error.status === 0) {
          // 5xx or timeout/network error — show toast, no redirect
          addToast({
            type: 'error',
            message: 'Session verification failed. Please check your connection.',
            duration: 5000,
          });
        }
      } else {
        // Unknown error (e.g., network failure) — show toast, no redirect
        addToast({
          type: 'error',
          message: 'Session verification failed. Please check your connection.',
          duration: 5000,
        });
      }
    },
    [setUnauthenticated, addToast],
  );

  const verify = useCallback(async () => {
    if (verificationInProgress.current) {
      return;
    }
    verificationInProgress.current = true;
    try {
      await verifySession();
    } catch (error) {
      handleVerificationError(error);
    } finally {
      verificationInProgress.current = false;
    }
  }, [handleVerificationError]);

  // Initial session verification on mount
  useEffect(() => {
    if (!initialVerificationDone.current) {
      initialVerificationDone.current = true;
      verify();
    }
  }, [verify]);

  // Re-verify on tab visibility change (hidden → visible)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        verify();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [verify]);
}
